/**
 * Calendar-date helpers for week bucketing (TRD 6.3): weeks start on Monday, dates are plain
 * `YYYY-MM-DD` strings, and "today" is taken in the planning timezone. All arithmetic is done in
 * UTC on the date string so no local-timezone drift creeps in.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** @param {string} iso */
function assertIso(iso) {
  if (!ISO_DATE.test(iso)) throw new TypeError(`expected YYYY-MM-DD, got ${iso}`);
}

/**
 * The calendar date at `instant` in `timeZone`, as `YYYY-MM-DD`.
 * @param {Date} instant
 * @param {string} timeZone  IANA name, e.g. America/Chicago
 */
export function isoDateInTimezone(instant, timeZone) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * @param {string} iso
 * @param {number} days  may be negative
 */
export function addDays(iso, days) {
  assertIso(iso);
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * The Monday on or before `iso`.
 * @param {string} iso
 */
export function mondayOf(iso) {
  assertIso(iso);
  const dow = new Date(`${iso}T00:00:00Z`).getUTCDay(); // 0 Sunday .. 6 Saturday
  return addDays(iso, dow === 0 ? -6 : 1 - dow);
}

/**
 * `count` consecutive week windows (Monday to Sunday) starting at the week containing `iso`.
 * @param {string} iso
 * @param {number} count
 * @returns {{ start: string, end: string }[]}
 */
export function weekWindows(iso, count) {
  const first = mondayOf(iso);
  return Array.from({ length: Math.max(0, count) }, (_, i) => {
    const start = addDays(first, 7 * i);
    return { start, end: addDays(start, 6) };
  });
}

/**
 * A `date` column as `YYYY-MM-DD`. pg returns dates as local-midnight Date objects, so the
 * local calendar fields are read, not the UTC instant; strings pass through trimmed.
 * @param {Date | string | null | undefined} value
 */
export function toIsoDate(value) {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) {
    const mm = String(value.getMonth() + 1).padStart(2, '0');
    const dd = String(value.getDate()).padStart(2, '0');
    return `${value.getFullYear()}-${mm}-${dd}`;
  }
  return String(value).slice(0, 10);
}

/**
 * The date `count` weekdays after `iso` (0 returns `iso` itself); weekends are stepped over, so
 * a Friday plus one weekday is the following Monday.
 * @param {string} iso
 * @param {number} count  zero or more
 */
export function addWeekdays(iso, count) {
  assertIso(iso);
  let date = iso;
  for (let left = Math.max(0, Math.trunc(count)); left > 0;) {
    date = addDays(date, 1);
    const dow = new Date(`${date}T00:00:00Z`).getUTCDay();
    if (dow !== 0 && dow !== 6) left--;
  }
  return date;
}

/**
 * The date `count` weekdays before `iso` (0 returns `iso` itself); weekends are stepped over, so
 * a Monday less one weekday is the Friday before.
 * @param {string} iso
 * @param {number} count  zero or more
 */
export function subtractWeekdays(iso, count) {
  assertIso(iso);
  let date = iso;
  for (let left = Math.max(0, Math.trunc(count)); left > 0;) {
    date = addDays(date, -1);
    const dow = new Date(`${date}T00:00:00Z`).getUTCDay();
    if (dow !== 0 && dow !== 6) left--;
  }
  return date;
}

/**
 * The weekdays (Monday to Friday) of an inclusive date range, as `YYYY-MM-DD` strings in order.
 * The daily grain (TRD 9) draws these: weekends hold no capacity and no demand.
 * @param {string} from
 * @param {string} to
 * @returns {string[]}
 */
export function weekdaysIn(from, to) {
  assertIso(from);
  assertIso(to);
  const out = [];
  for (let date = from; date <= to; date = addDays(date, 1)) {
    const dow = new Date(`${date}T00:00:00Z`).getUTCDay();
    if (dow !== 0 && dow !== 6) out.push(date);
  }
  return out;
}

/**
 * Inclusive day count between two dates.
 * @param {string} from
 * @param {string} to
 */
export function daysBetween(from, to) {
  assertIso(from);
  assertIso(to);
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/**
 * The instant at which the calendar day `iso` begins in `timeZone`. Two passes over the zone's
 * UTC offset settle days that start inside a daylight-saving change.
 * @param {string} iso
 * @param {string} timeZone
 */
export function startOfDayInTimezone(iso, timeZone) {
  assertIso(iso);
  let guess = Date.parse(`${iso}T00:00:00Z`);
  for (let i = 0; i < 2; i++) {
    guess -= offsetMs(new Date(guess), timeZone);
    if (
      isoDateInTimezone(new Date(guess), timeZone) === iso &&
      wallClockMinutes(new Date(guess), timeZone) === 0
    )
      break;
  }
  return new Date(guess);
}

/**
 * The hour on the wall clock of `timeZone` at `instant`, 0 to 23. What a job scheduled at "07:00
 * planning time" compares itself with (TRD 12.1).
 * @param {Date} instant
 * @param {string} timeZone
 */
export function hourInTimezone(instant, timeZone) {
  return Math.floor(wallClockMinutes(instant, timeZone) / 60);
}

/** Minutes past midnight on the wall clock of `timeZone` at `instant`. */
function wallClockMinutes(instant, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return get('hour') * 60 + get('minute');
}

/** The zone's offset from UTC at `instant`, in milliseconds (positive east of Greenwich). */
function offsetMs(instant, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}
